import { Slot } from '@radix-ui/react-slot'
import clsx from 'clsx'
import { forwardRef } from 'react'

import { type IconProp } from 'slab/types'

import { useTabsContext } from './context'

interface TabProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  action?: React.ReactNode;
  children?: React.ReactNode;
  disabled?: boolean;
  icon?: IconProp;
  name: string;
  status?: React.ReactNode;
}

export const Tab = forwardRef<HTMLButtonElement, TabProps>(
  (
    {
      action,
      children,
      disabled,
      icon: Icon,
      name,
      status,
      ...props
    },
    forwardedRef
  ) => {
    const { activeTab, rootId, setActiveTab } = useTabsContext()

    const tabId = `${rootId}-${name}-tab`
    const panelId = `${rootId}-${name}-panel`

    const isActive = activeTab === name

    function handleClick () {
      setActiveTab(name)
    }

    return (
      <div
        className={clsx(
          'group/tab',
          'flex shrink-0 items-center rounded-t-lg',
          'body-bold',
          'bg-transparent hover:bg-gray-100 has-[[role=tab]:disabled]:bg-transparent',
          'border-b-2 border-transparent hover:border-gray-300 has-[[role=tab]:disabled]:border-transparent data-[state=active]:border-gray-800 data-[state=active]:hover:border-gray-800',
          'transition'
        )}
        data-tab
        data-state={isActive ? 'active' : 'inactive'}
      >
        <button
          {...props}
          aria-controls={panelId}
          aria-selected={isActive}
          className={clsx(
            'group/tab-trigger',
            'p-2 flex',
            'outline-none',
            'text-gray-500 hover:text-gray-800 group-data-[state=active]/tab:text-gray-800 disabled:text-gray-300',
            'group-data-[state=active]/tab:cursor-default disabled:cursor-default'
          )}
          data-name={name}
          id={tabId}
          role='tab'
          disabled={disabled}
          onClick={handleClick}
          ref={forwardedRef}
          tabIndex={isActive ? 0 : -1}
          value={name}
        >
          <span className='rounded-md flex items-center gap-2 outline outline-offset-2 outline-2 outline-transparent group-focus-visible/tab-trigger:outline-thatch-500'>
            <span className='flex items-center gap-1'>
              {!!Icon && (
                <span className='text-gray-500'>
                  <Icon aria-hidden size={16} />
                </span>
              )}
              {!!children && <span>{children}</span>}
            </span>
            {!!status && (
              <span className='flex items-center h-5'>{status}</span>
            )}
          </span>
        </button>
        {!!action && (
        /**
           * Note: axe-core will flag any action `<button>` elements as
           * violations of the `aria-required-children` rule. There's
           * no accepted ARIA pattern to associate secondary actions with tabs
           * at the moment, though a new `aria-action` attribute has been
           * proposed.
           *
           * Per [this experimental example from W3C](https://www.w3.org/WAI/ARIA/apg/patterns/tabs/examples/tabs-actions/)
           * and the tests performed by [Sarah Higley](https://sarahmhigley.com/)
           * [here](https://a11y-screenreader-demos.netlify.app/studies/secondary-actions/),
           * our approach of rendering secondary actions as a sibling of the tab,
           * rather than nested within the tab, is the best approach at the moment.
           *
           * For now, we'll just ignore the axe-core violation and hope for
           * a standardized solution accepted by AT (accessibility technology)
           * vendors in the future.
           *
           * See https://github.com/w3c/aria/issues/1440 for more context.
           */
          <span className='flex items-center h-5 pr-2'>
            <Slot tabIndex={isActive ? 0 : -1}>{action}</Slot>
          </span>
        )}
      </div>
    )
  }
)

Tab.displayName = 'Tabs.Tab'
